import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Linkedin } from 'lucide-react';

export default function PublicNav({ currentPage }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Handle navigation to homepage sections from any page
  const handleSectionNavigation = (sectionId) => {
    setMobileMenuOpen(false);
    
    // Check if we're on the homepage
    if (window.location.pathname === '/') {
      // Already on homepage, just scroll to section
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage with hash, then scroll after page loads
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <>
      {/* Fixed Navigation - Always dark blue */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-700 bg-slate-800/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo - Home Button */}
            <a 
              href="/" 
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0 mr-4 sm:mr-6 lg:mr-10 cursor-pointer" 
              title="Go to Home"
            >
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="sm:hidden"
                style={{ height: '38px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden sm:block md:hidden"
                style={{ height: '46px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden md:block lg:hidden"
                style={{ height: '54px', width: 'auto' }}
              />
              <img 
                src="/assets/seedsynch-logo-lockup.svg" 
                alt="SeedSynch - Go to Home" 
                className="hidden lg:block"
                style={{ height: '64px', width: 'auto' }}
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              <button 
                onClick={() => handleSectionNavigation('how-it-works')}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${currentPage === 'how-it-works' ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                What to Expect
              </button>
              <Link 
                to="/pricing"
                className={`text-sm font-medium transition-colors ${currentPage === 'pricing' ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                Pricing
              </Link>
              <Link 
                to="/blog" 
                className={`text-sm font-medium transition-colors ${currentPage === 'blog' ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                Blog
              </Link>
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-colors ${currentPage === 'about' ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                About
              </Link>
              <Link 
                to="/faq" 
                className={`text-sm font-medium transition-colors ${currentPage === 'faq' ? 'text-blue-400' : 'text-slate-300 hover:text-blue-400'}`}
              >
                FAQ
              </Link>
              <a 
                href="https://www.linkedin.com/company/seedsynch" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-slate-300 hover:text-blue-400 transition-colors text-sm"
              >
                <span className="hidden xl:inline">Follow SeedSynch on</span>
                <Linkedin size={18} />
              </a>
            </div>

            {/* Mobile menu button - white on dark nav */}
            <button
              className="lg:hidden p-2 text-white hover:text-blue-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-slate-800 border-t border-slate-700 py-4 space-y-3">
              <button 
                onClick={() => handleSectionNavigation('how-it-works')}
                className="block w-full text-left text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                What to Expect
              </button>
              <Link 
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                Pricing
              </Link>
              <Link 
                to="/blog" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                Blog
              </Link>
              <Link 
                to="/about" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                About
              </Link>
              <Link 
                to="/faq" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                FAQ
              </Link>
              <a 
                href="https://www.linkedin.com/company/seedsynch" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-300 hover:text-blue-400 transition-colors text-sm font-medium py-2"
              >
                <Linkedin size={18} /> LinkedIn
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer for fixed nav - matches nav height */}
      <div className="h-16 lg:h-[72px]"></div>
    </>
  );
}
